import {
  CoolerProductsColumnId,
  CoolersListColumnId,
  ManagedColumnId,
} from './constants'

export type TableColumnVisibilitySettings = {
  tableId: string
  tenantHiddenColumns: string[]
  userHiddenColumns: string[]
  effectiveHiddenColumns: string[]
}

export const EMPTY_TABLE_COLUMN_VISIBILITY_SETTINGS: TableColumnVisibilitySettings =
  {
    tableId: '',
    tenantHiddenColumns: [],
    userHiddenColumns: [],
    effectiveHiddenColumns: [],
  }

export const getSupportedColumnIdsSet = <TColumnId extends string>(
  supportedColumns: readonly TColumnId[],
) => new Set<string>(supportedColumns)

export const normalizeTableColumnVisibilitySettings = <
  TColumnId extends string,
>(
  settings: TableColumnVisibilitySettings | null | undefined,
  supportedColumns: readonly TColumnId[],
): TableColumnVisibilitySettings => {
  if (!settings) {
    return EMPTY_TABLE_COLUMN_VISIBILITY_SETTINGS
  }

  const supportedColumnIds = getSupportedColumnIdsSet(supportedColumns)
  const filterKnown = (columnIds: string[]) =>
    columnIds.filter(columnId => supportedColumnIds.has(columnId))

  return {
    tableId: settings.tableId,
    tenantHiddenColumns: filterKnown(settings.tenantHiddenColumns),
    userHiddenColumns: filterKnown(settings.userHiddenColumns),
    effectiveHiddenColumns: filterKnown(settings.effectiveHiddenColumns),
  }
}

export const isColumnEffectivelyHidden = (
  settings: TableColumnVisibilitySettings | null | undefined,
  columnId: string,
) => Boolean(settings?.effectiveHiddenColumns.includes(columnId))

const toggleColumnId = (
  columnIds: string[],
  columnId: string,
  hidden: boolean,
) => {
  if (hidden) {
    return columnIds.includes(columnId) ? columnIds : [...columnIds, columnId]
  }

  return columnIds.filter(id => id !== columnId)
}

export const buildNextUserHiddenColumns = (
  settings: TableColumnVisibilitySettings,
  columnId: string,
  hidden: boolean,
) => toggleColumnId(settings.userHiddenColumns, columnId, hidden)

export const buildNextTenantHiddenColumns = (
  settings: TableColumnVisibilitySettings,
  columnId: string,
  hidden: boolean,
) => toggleColumnId(settings.tenantHiddenColumns, columnId, hidden)

export const getVisibleColumnIds = <TColumnId extends string>(
  supportedColumns: readonly TColumnId[],
  settings: TableColumnVisibilitySettings | null | undefined,
) =>
  supportedColumns.filter(
    columnId => !isColumnEffectivelyHidden(settings, columnId),
  )

export type CoolerManagedColumnId =
  | CoolersListColumnId
  | CoolerProductsColumnId
  | ManagedColumnId
